// assets/embroidery-configurator.js
import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/** Pixel width of the placement image attached to the order for the artisans. */
const ORDER_IMAGE_WIDTH = 1280;

/** Pixel width of the preview shown in the product gallery (~2× a desktop gallery column, sharp on retina). */
const GALLERY_IMAGE_WIDTH = 1600;

/**
 * @typedef {object} ThreadColor
 * @property {string} id
 * @property {string} label
 * @property {string} hex
 */

/**
 * @typedef {object} EmbroideryFont
 * @property {string} id
 * @property {string} label
 * @property {string} family
 */

/**
 * @typedef {object} EmbroideryProductConfig
 * @property {string} [referenceImage]
 * @property {{x: number, y: number, width: number, height: number}} [area]
 * @property {number} [maxCharacters]
 * @property {EmbroideryFont[]} [fonts]
 * @property {ThreadColor[]} [threadColors]
 */

/**
 * @typedef {object} EmbroideryConfiguratorRefs
 * @property {HTMLInputElement} textInput - The embroidery text input.
 * @property {HTMLElement} characterCount - The character count display.
 * @property {HTMLElement} colorField - Wrapper for the thread color swatches.
 * @property {HTMLElement} colorSwatches - Container for thread color swatch buttons.
 * @property {HTMLElement} styleField - Wrapper for the font style options.
 * @property {HTMLElement} styleOptions - Container for font style buttons.
 * @property {HTMLInputElement} hiddenColor - Hidden input carrying the selected thread color.
 * @property {HTMLInputElement} hiddenStyle - Hidden input carrying the selected font style.
 * @property {HTMLElement} errorMessage - Accessible error message region.
 * @property {HTMLElement} previewWrapper - Wrapper that hosts the canvas preview.
 * @property {HTMLCanvasElement} previewCanvas - Canvas element for the Fabric.js preview.
 * @property {HTMLScriptElement} assetImageMap - JSON script tag mapping reference image filenames to asset URLs.
 * @property {HTMLInputElement} hiddenImage - Hidden file input carrying the embroidery placement preview image.
 */

/**
 * Configurador de bordado: texto + color de hilo + estilo de letra,
 * enviados como line item properties al form de producto.
 *
 * @extends Component<EmbroideryConfiguratorRefs>
 */
class EmbroideryConfigurator extends Component {
  requiredRefs = ['textInput', 'characterCount', 'errorMessage'];

  /** @type {ThreadColor | undefined} */
  #selectedColor;

  /** @type {EmbroideryFont | undefined} */
  #selectedFont;

  /** @type {EmbroideryProductConfig | undefined} */
  #config;

  /** @type {import('@theme/embroidery-canvas').EmbroideryCanvas | undefined} */
  #canvas;

  /** Reference image resolved from embroidery-config.json, used when the current variant has no image of its own. @type {string | undefined} */
  #defaultReferenceImageUrl;

  /** Object URL of the high-quality render shown over the product gallery once the customer taps "Listo". @type {string | undefined} */
  #galleryPreviewUrl;

  /** Set when the customer chose "Ver fotos del producto", so variant changes don't bring the overlay back. */
  #galleryPreviewDismissed = false;

  /**
   * The ancestor that the variant picker's ProductSelectEvent actually
   * bubbles through. Resolved starting *above* this component's own
   * `dialog-component` (the "Personalizar" modal) — searching from here
   * directly would match that inner dialog instead of the surrounding
   * product section, since it's a closer ancestor than the real target.
   * @type {Element | null | undefined}
   */
  #productSelectScope;

  connectedCallback() {
    super.connectedCallback();

    this.#applyPropertyNames();
    this.#applyMaxLength(Number(this.dataset.maxLengthFallback) || 40);
    this.#updateCharacterCount();

    const ownDialog = this.closest('dialog-component');
    this.#productSelectScope = (ownDialog?.parentElement ?? this).closest(
      '.shopify-section, dialog, product-card'
    );
    this.#productSelectScope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#loadConfig();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#productSelectScope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    if (this.#galleryPreviewUrl) URL.revokeObjectURL(this.#galleryPreviewUrl);
  }

  /**
   * Swaps the preview's reference photo to the selected variant's own image
   * (e.g. the garment in the chosen color) as soon as the customer picks it
   * on the product page — before they ever open the "Personalizar" modal.
   * Falls back to the product's default reference image when the variant
   * doesn't have its own photo.
   *
   * @param {import('@shopify/events').ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const imageUrl = detail?.resource?.featured_image?.src || this.#defaultReferenceImageUrl;
        if (!imageUrl || !this.#canvas) return;

        this.#canvas
          .setReferenceImage(imageUrl)
          .then(async () => {
            // Keep an already-applied design in the gallery, redrawn on the
            // newly selected garment color.
            if (!this.#galleryPreviewUrl) return;
            await this.#updateGalleryPreview();
            this.#mountGalleryPreview();
          })
          .catch(() => {});
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[embroidery-configurator] Variant select promise rejected:', error);
        }
      });
  };

  /**
   * Validates the current selections and, if valid, submits the product
   * form (same AJAX add-to-cart flow as the page's own "Agregar al
   * carrito" button) and closes the modal. Called by the drawer's "Listo"
   * button.
   */
  async applyAndAddToCart() {
    if (!this.#validateSelections()) return;

    const form = /** @type {HTMLFormElement | null} */ (document.getElementById(this.dataset.formId || ''));
    if (!form) return;

    // Keep the modal open (and point at the problem) instead of closing it
    // over a form that requestSubmit() would silently refuse to send.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    await Promise.all([this.#attachPreviewImage(), this.#updateGalleryPreview()]);
    this.#galleryPreviewDismissed = false;
    this.#mountGalleryPreview();

    // requestSubmit() runs native constraint validation first (e.g. the
    // required embroidery text field) and only dispatches `submit` — which
    // product-form-component listens for — if the form is valid.
    form.requestSubmit();

    const dialog = /** @type {{ closeDialog?: () => void } | null} */ (this.closest('dialog-component'));
    dialog?.closeDialog?.();
  }

  /**
   * Renders the canvas to a PNG and loads it into the hidden `hiddenImage`
   * file input so the AJAX add-to-cart FormData submit (product-form.js
   * already builds a real `FormData(form)`, which natively picks up file
   * inputs) carries it as a `properties[...]` file upload — Shopify
   * uploads it and stores the resulting URL as the property value, so
   * whoever embroiders the order can see the exact placement.
   */
  async #attachPreviewImage() {
    const { hiddenImage } = this.refs;
    if (!hiddenImage || !this.#canvas) return;

    try {
      const blob = await this.#canvas.exportPreviewImage({ width: ORDER_IMAGE_WIDTH });
      const file = new File([blob], 'bordado-preview.png', { type: 'image/png' });
      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(file);
      hiddenImage.files = dataTransfer.files;
    } catch (error) {
      console.warn('[embroidery-configurator] Failed to attach preview image:', error);
    }
  }

  /**
   * Re-renders the customer's design (without the dashed placement guide)
   * at gallery resolution and keeps it as an object URL, ready to be shown
   * where the product photos are.
   */
  async #updateGalleryPreview() {
    if (!this.#canvas) return;

    try {
      const blob = await this.#canvas.exportPreviewImage({ width: GALLERY_IMAGE_WIDTH, showGuide: false });
      if (this.#galleryPreviewUrl) URL.revokeObjectURL(this.#galleryPreviewUrl);
      this.#galleryPreviewUrl = URL.createObjectURL(blob);
    } catch (error) {
      console.warn('[embroidery-configurator] Failed to render gallery preview:', error);
    }
  }

  /**
   * Shows the finished design on top of the product's media gallery. The
   * gallery itself is swapped out wholesale by media-gallery.js on every
   * variant change, so this is re-run afterwards instead of assuming the
   * overlay is still in the DOM.
   */
  #mountGalleryPreview() {
    const gallery = this.#productSelectScope?.querySelector('media-gallery');
    if (!gallery || !this.#galleryPreviewUrl || this.#galleryPreviewDismissed) return;

    let overlay = gallery.querySelector(':scope > .embroidery-gallery-preview');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'embroidery-gallery-preview';

      const image = document.createElement('img');
      image.className = 'embroidery-gallery-preview__image';
      image.alt = this.dataset.galleryImageAlt || '';

      const actions = document.createElement('div');
      actions.className = 'embroidery-gallery-preview__actions';

      const editButton = document.createElement('button');
      editButton.type = 'button';
      editButton.className = 'button button-secondary';
      editButton.textContent = this.dataset.galleryEditLabel || 'Editar bordado';
      editButton.addEventListener('click', () => {
        const dialog = /** @type {{ showDialog?: () => void } | null} */ (this.closest('dialog-component'));
        dialog?.showDialog?.();
      });

      const hideButton = document.createElement('button');
      hideButton.type = 'button';
      hideButton.className = 'button button-secondary';
      hideButton.textContent = this.dataset.galleryHideLabel || 'Ver fotos del producto';
      hideButton.addEventListener('click', () => {
        this.#galleryPreviewDismissed = true;
        overlay?.remove();
      });

      actions.append(editButton, hideButton);
      overlay.append(image, actions);
      gallery.append(overlay);
    }

    const image = /** @type {HTMLImageElement} */ (overlay.querySelector('.embroidery-gallery-preview__image'));
    image.src = this.#galleryPreviewUrl;
  }

  /** @returns {boolean} */
  #validateSelections() {
    const missingColor = this.#config?.threadColors?.length && !this.#selectedColor;
    const missingStyle = this.#config?.fonts?.length && !this.#selectedFont;

    if (this.dataset.required === 'true' && (missingColor || missingStyle)) {
      this.#showError(this.dataset.selectionRequiredMessage || '');
      return false;
    }

    this.#clearError();
    return true;
  }

  /** @param {InputEvent} _event */
  handleTextInput(_event) {
    this.#updateCharacterCount();
    this.#clearError();
    this.#canvas?.setText(this.refs.textInput.value);
  }

  async #loadConfig() {
    const configUrl = this.dataset.configUrl;
    const productHandle = this.dataset.productHandle;
    if (!configUrl || !productHandle) return;

    try {
      const response = await fetch(configUrl);
      if (!response.ok) return;

      /** @type {Record<string, EmbroideryProductConfig>} */
      const allConfig = await response.json();
      const config = allConfig[productHandle];
      if (!config) return;

      this.#config = config;

      if (config.maxCharacters) {
        this.#applyMaxLength(config.maxCharacters);
        this.#updateCharacterCount();
      }

      if (config.threadColors?.length) {
        this.#renderThreadColors(config.threadColors);
      }

      if (config.fonts?.length) {
        this.#renderFontStyles(config.fonts);
      }

      if (config.referenceImage && config.area) {
        await this.#initCanvas(config.referenceImage, config.area);
      }
    } catch (error) {
      console.warn('[embroidery-configurator] Failed to load product config:', error);
    }
  }

  /**
   * @param {string} referenceImage
   * @param {{x: number, y: number, width: number, height: number}} area
   */
  async #initCanvas(referenceImage, area) {
    const canvasModuleUrl = this.dataset.canvasModuleUrl;
    if (!canvasModuleUrl) return;

    const imageUrl = this.#resolveAssetUrl(referenceImage);
    this.#defaultReferenceImageUrl = imageUrl;

    try {
      const { EmbroideryCanvas } = await import(canvasModuleUrl);
      this.#canvas = new EmbroideryCanvas(this.refs.previewCanvas);
      await this.#canvas.render({ imageUrl, area });

      this.refs.previewWrapper.hidden = false;
      this.#canvas.setText(this.refs.textInput.value);
      if (this.#selectedColor) this.#canvas.setColor(this.#selectedColor.hex);
      if (this.#selectedFont) this.#canvas.setFontFamily(this.#selectedFont.family);
    } catch (error) {
      console.warn('[embroidery-configurator] Failed to initialize canvas preview:', error);
    }
  }

  /** @param {string} filename */
  #resolveAssetUrl(filename) {
    const { assetImageMap } = this.refs;
    if (!assetImageMap) return filename;

    try {
      const map = JSON.parse(assetImageMap.textContent || '{}');
      return map[filename] || filename;
    } catch {
      return filename;
    }
  }

  #applyPropertyNames() {
    const { textInput, hiddenColor, hiddenStyle, hiddenImage } = this.refs;
    const keyText = this.dataset.propertyKeyText || 'Texto bordado';
    const keyColor = this.dataset.propertyKeyColor || 'Color de hilo';
    const keyStyle = this.dataset.propertyKeyStyle || 'Estilo de letra';
    // Leading "_" = private property: Shopify hides it from the customer's
    // cart and checkout but keeps it on the order in the admin, which is the
    // only place the placement image is needed (for the artisans).
    const keyImage = `_${(this.dataset.propertyKeyImage || 'Vista previa del bordado').replace(/^_+/, '')}`;

    textInput.name = `properties[${keyText}]`;
    if (hiddenColor) hiddenColor.name = `properties[${keyColor}]`;
    if (hiddenStyle) hiddenStyle.name = `properties[${keyStyle}]`;
    if (hiddenImage) hiddenImage.name = `properties[${keyImage}]`;
  }

  /** @param {number} maxLength */
  #applyMaxLength(maxLength) {
    this.refs.textInput.maxLength = maxLength;
  }

  #updateCharacterCount() {
    const { textInput, characterCount } = this.refs;
    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    characterCount.textContent = template
      .replace('[current]', textInput.value.length.toString())
      .replace('[max]', textInput.maxLength.toString());
  }

  /** @param {ThreadColor[]} colors */
  #renderThreadColors(colors) {
    const { colorField, colorSwatches } = this.refs;
    if (!colorField || !colorSwatches) return;

    colorSwatches.replaceChildren();

    for (const color of colors) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = '__swatch';
      button.style.backgroundColor = color.hex;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.setAttribute('aria-label', color.label);
      button.dataset.colorId = color.id;
      button.addEventListener('click', () => this.#selectColor(color, button));
      colorSwatches.append(button);
    }

    colorField.hidden = false;

    if (colors[0]) {
      const firstButton = /** @type {HTMLButtonElement} */ (colorSwatches.firstElementChild);
      this.#selectColor(colors[0], firstButton);
    }
  }

  /**
   * @param {ThreadColor} color
   * @param {HTMLButtonElement} button
   */
  #selectColor(color, button) {
    this.#selectedColor = color;
    this.refs.hiddenColor.value = color.label;
    this.#canvas?.setColor(color.hex);

    for (const sibling of button.parentElement?.children ?? []) {
      sibling.setAttribute('aria-checked', sibling === button ? 'true' : 'false');
    }

    this.#clearError();
  }

  /** @param {EmbroideryFont[]} fonts */
  #renderFontStyles(fonts) {
    const { styleField, styleOptions } = this.refs;
    if (!styleField || !styleOptions) return;

    styleOptions.replaceChildren();

    for (const font of fonts) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = '__style-option';
      button.style.fontFamily = font.family;
      button.textContent = font.label;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', 'false');
      button.dataset.fontId = font.id;
      button.addEventListener('click', () => this.#selectFont(font, button));
      styleOptions.append(button);
    }

    styleField.hidden = false;

    if (fonts[0]) {
      const firstButton = /** @type {HTMLButtonElement} */ (styleOptions.firstElementChild);
      this.#selectFont(fonts[0], firstButton);
    }
  }

  /**
   * @param {EmbroideryFont} font
   * @param {HTMLButtonElement} button
   */
  #selectFont(font, button) {
    this.#selectedFont = font;
    this.refs.hiddenStyle.value = font.label;
    this.#canvas?.setFontFamily(font.family);

    for (const sibling of button.parentElement?.children ?? []) {
      sibling.setAttribute('aria-checked', sibling === button ? 'true' : 'false');
    }

    this.#clearError();
  }

  /** @param {string} message */
  #showError(message) {
    const { errorMessage } = this.refs;
    errorMessage.textContent = message;
    errorMessage.classList.remove('hidden');
  }

  #clearError() {
    const { errorMessage } = this.refs;
    errorMessage.textContent = '';
    errorMessage.classList.add('hidden');
  }
}

if (!customElements.get('embroidery-configurator-component')) {
  customElements.define('embroidery-configurator-component', EmbroideryConfigurator);
}
